import React, { forwardRef, useEffect, useCallback, useRef, useState } from 'react';
import { CameraContext } from '../../utils/CameraContext';
import { screenToWorld, worldToScreen } from '../../utils/coordinates';

const MIN_ZOOM = 0.25;
const MAX_ZOOM = 2;
const ZOOM_SPEED = 0.0002;
const PARALLAX_FACTOR = 0.3;
const GRID_OPACITY = 0.5;
const BACKGROUND_SCALE_PERCENT = 125;
const GRID_COLOR = '#666';
const GRID_SIZE = 40;

const DraggableCanvas = forwardRef(({ children, onTransformChange, canvasTransform, disableNavigation = false, onDimensionsChange }, ref) => {
  const containerRef = useRef(null);
  const [randomBackground, setRandomBackground] = useState(null);
  const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 });

  const isPanning = useRef(false);
  const isShiftPressed = useRef(false);
  const initialCamera = useRef({ x: 0, y: 0 });
  const startMouseScreen = useRef({ x: 0, y: 0 });

  useEffect(() => {
    // Once container is known, get dimensions and report them
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setViewportSize({ width: rect.width, height: rect.height });
      onDimensionsChange?.(rect.width, rect.height);
    }
  }, [onDimensionsChange]);

  useEffect(() => {
    try {
      const backgroundContext = require.context('../../assets/backgrounds', false, /\.png$/);
      const backgroundKeys = backgroundContext.keys();
      if (backgroundKeys.length > 0) {
        const randomKey = backgroundKeys[Math.floor(Math.random() * backgroundKeys.length)];
        const chosenBackground = backgroundContext(randomKey);
        setRandomBackground(chosenBackground);
      } else {
        setRandomBackground(null);
      }
    } catch (error) {
      console.error('Error loading backgrounds:', error);
      setRandomBackground(null);
    }
  }, []);

  useEffect(() => {
    const handleDown = (e) => { if (e.key === 'Shift') isShiftPressed.current = true; };
    const handleUp = (e) => { if (e.key === 'Shift') isShiftPressed.current = false; };
    window.addEventListener('keydown', handleDown);
    window.addEventListener('keyup', handleUp);
    return () => {
      window.removeEventListener('keydown', handleDown);
      window.removeEventListener('keyup', handleUp);
    };
  }, []);

  const handleMouseDown = useCallback((e) => {
    if (disableNavigation) return;
    if (!isShiftPressed.current || !viewportSize.width || !viewportSize.height) return;

    isPanning.current = true;
    initialCamera.current = { x: canvasTransform.translateX, y: canvasTransform.translateY };
    startMouseScreen.current = { x: e.clientX, y: e.clientY };
  }, [disableNavigation, canvasTransform, viewportSize]);

  const handleMouseMove = useCallback((e) => {
    if (disableNavigation) return;
    if (!isPanning.current || !viewportSize.width || !viewportSize.height) return;

    const dx = e.clientX - startMouseScreen.current.x;
    const dy = e.clientY - startMouseScreen.current.y;

    // Compute new camera position
    const newX = initialCamera.current.x - dx / canvasTransform.scale;
    const newY = initialCamera.current.y - dy / canvasTransform.scale;

    if (onTransformChange) {
      onTransformChange({
        scale: canvasTransform.scale,
        translateX: newX,
        translateY: newY
      });
    }
  }, [disableNavigation, canvasTransform, onTransformChange, viewportSize]);

  const handleMouseUp = useCallback(() => {
    if (disableNavigation) return;
    isPanning.current = false;
  }, [disableNavigation]);

  const handleWheel = useCallback((e) => {
    if (disableNavigation) return;
    if (!isShiftPressed.current || !viewportSize.width || !viewportSize.height) return;
    const oldScale = canvasTransform.scale;
    const delta = -e.deltaY * ZOOM_SPEED;
    const newScale = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, oldScale + delta));

    const viewportWidth = viewportSize.width;
    const viewportHeight = viewportSize.height;

    const { x: cx, y: cy } = screenToWorld(
      viewportWidth / 2,
      viewportHeight / 2,
      canvasTransform.translateX,
      canvasTransform.translateY,
      oldScale
    );

    const newCameraX = cx - (viewportWidth / (2 * newScale));
    const newCameraY = cy - (viewportHeight / (2 * newScale));

    onTransformChange?.({
      scale: newScale,
      translateX: newCameraX,
      translateY: newCameraY
    });
  }, [disableNavigation, canvasTransform, onTransformChange, viewportSize]);

  React.useImperativeHandle(ref, () => ({
    ...ref.current,
    getTransform: () => ({
      scale: canvasTransform.scale,
      translateX: canvasTransform.translateX,
      translateY: canvasTransform.translateY
    })
  }));

  const cameraValue = {
    cameraX: canvasTransform.translateX,
    cameraY: canvasTransform.translateY,
    cameraScale: canvasTransform.scale,
    worldToScreen: (x, y) => worldToScreen(x, y, canvasTransform.translateX, canvasTransform.translateY, canvasTransform.scale),
    screenToWorld: (sx, sy) => screenToWorld(sx, sy, canvasTransform.translateX, canvasTransform.translateY, canvasTransform.scale)
  };

  const parallaxBackgroundStyle = {
    transformOrigin: '0 0',
    transform: `translate(${-cameraValue.cameraX * cameraValue.cameraScale * PARALLAX_FACTOR}px, ${-cameraValue.cameraY * cameraValue.cameraScale * PARALLAX_FACTOR}px) scale(${cameraValue.cameraScale})`
  };

  const foregroundTransformStyle = {
    transformOrigin: '0 0',
    transform: `translate(${-cameraValue.cameraX * cameraValue.cameraScale}px, ${-cameraValue.cameraY * cameraValue.cameraScale}px) scale(${cameraValue.cameraScale})`
  };

  let backgroundStyle = {};
  if (viewportSize.width && viewportSize.height && randomBackground) {
    const backgroundWidth = viewportSize.width * (BACKGROUND_SCALE_PERCENT / 100);
    const backgroundHeight = viewportSize.height * (BACKGROUND_SCALE_PERCENT / 100);

    const bgLeft = -(backgroundWidth / 2) + (viewportSize.width / 2);
    const bgTop = -(backgroundHeight / 2) + (viewportSize.height / 2);

    backgroundStyle = {
      ...parallaxBackgroundStyle,
      position: 'absolute',
      width: `${backgroundWidth}px`,
      height: `${backgroundHeight}px`,
      left: `${bgLeft}px`,
      top: `${bgTop}px`,
      backgroundImage: `url(${randomBackground})`,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      backgroundSize: 'cover'
    };
  }

  return (
    <CameraContext.Provider value={cameraValue}>
      <div
        ref={containerRef}
        className="w-full h-full relative bg-black"
        style={{
          height: '100%',
          overflow: 'clip', // Use 'clip' instead of 'hidden' to truly prevent programmatic scroll
          cursor: disableNavigation ? 'default' : (isShiftPressed.current ? (isPanning.current ? 'grabbing' : 'grab') : 'default')
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onWheel={handleWheel}
        onScroll={(e) => {
          // Reset any programmatic scroll attempts (backup for browsers where clip doesn't work)
          if (e.target.scrollTop !== 0 || e.target.scrollLeft !== 0) {
            e.target.scrollTop = 0;
            e.target.scrollLeft = 0;
          }
        }}
      >
        {viewportSize.width && viewportSize.height && randomBackground && (
          <div className="pointer-events-none parallax-background-layer" style={backgroundStyle}></div>
        )}
        <div
          className="pointer-events-none absolute"
          style={{
            ...foregroundTransformStyle,
            opacity: GRID_OPACITY,
            width: '100000px',
            height: '100000px',
            left: '-50000px',
            top: '-50000px',
            backgroundColor: 'black',
            backgroundImage: `
              linear-gradient(to right, ${GRID_COLOR} 1px, transparent 1px),
              linear-gradient(to bottom, ${GRID_COLOR} 1px, transparent 1px)
            `,
            backgroundSize: `${GRID_SIZE}px ${GRID_SIZE}px`
          }}
        ></div>

        <div className="scene-layer relative w-full h-full" style={foregroundTransformStyle}>
          {children}
        </div>
      </div>
    </CameraContext.Provider>
  );
});

DraggableCanvas.displayName = 'DraggableCanvas';
export default DraggableCanvas;
